<template>
  <div class="onboarding-summary">
    <div class="onboarding-summary__backgrounds-mount">
      <LearningSetupSummaryBackgrounds />
    </div>

    <Logo class="onboarding-summary__logo" :withText="true" />

    <Layout
      :title="page.title"
      :subtitle="page.subtitle"
      :reverseTitleSubtitle="true"
      :scrollDefaultSlot="true"
    >
      <div class="onboarding-summary__stack">
        <LearningSetupSummaryChoicesSection
          v-if="summaryRows.length"
          :rows="summaryRows"
        />
        <LearningSetupSummaryBooksSection
          v-if="likedSlugs.length"
          :slugs="likedSlugs"
          :cover-url="getBookCover"
        />
      </div>
    </Layout>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { QuestionnaireView } from '@/models/onboarding'
import type { LearningSetupSummaryPage } from '@/models/onboarding.types'
import {
  buildLearningSetupSummaryRows,
  getLikedBookSlugsFromUserChoices,
} from '@/models/onboarding.personalization'

import Layout from '../Layout.vue'
import Logo from '@/components/ui/Logo.vue'
import LearningSetupSummaryBackgrounds from './LearningSetupSummaryBackgrounds.vue'
import LearningSetupSummaryBooksSection from './LearningSetupSummaryBooksSection.vue'
import LearningSetupSummaryChoicesSection from './LearningSetupSummaryChoicesSection.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as LearningSetupSummaryPage
})

const summaryRows = computed(() =>
  buildLearningSetupSummaryRows(props.view.userChoices),
)

const likedSlugs = computed(() =>
  getLikedBookSlugsFromUserChoices(props.view.userChoices),
)

function getBookCover(slug: string): string {
  return props.view.getBookCover(slug)
}
</script>

<style lang="scss" scoped>
.onboarding-summary {
  position: relative;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  --onboarding-padding: 30px;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  height: 100%;

  &__backgrounds-mount {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 0;
    overflow: visible;
    pointer-events: none;

    /* Let the top glow reach into the questionnaire header / status-bar zone. */
    @media (max-width: $w-medium-s) {
      top: calc(-1 * (env(safe-area-inset-top, 0px) + 88px));
    }
  }

  &__logo {
    position: relative;
    z-index: 2;
    display: block;
    flex-shrink: 0;
    height: 24px;
    margin-top: 22px;
    margin-inline: auto;
    animation: fadeIn 1s forwards;
    @supports (padding-top: env(safe-area-inset-top)) {
      margin-top: max(22px, env(safe-area-inset-top));
    }
  }

  :deep(.onboarding-layout) {
    position: relative;
    z-index: 1;
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
  }

  :deep(.onboarding-layout__text) {
    position: relative;
    z-index: 1;
  }

  :deep(.onboarding-layout__default-slot) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1 1 auto;
    min-height: 0;
    margin-top: 24px;
  }

  :deep(.onboarding-layout__subtitle) {
    font-size: 18px;
    line-height: 24px;
    font-style: italic;
  }

  &__main {
    position: relative;
    width: 100%;
  }

  &__stack {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 12px;
  }

  &__footer-note {
    flex-shrink: 0;
    margin: 0 auto;
    padding-top: 24px;
    font-size: 16px;
    line-height: 22px;
    text-align: center;
    color: $c-black-2;
  }
}
</style>
